import { useFormatter, type ValueFormat } from "@/artifacts/format";
import type { Artifact } from "@/artifacts/types";
import { cn } from "@/lib/cn";

/**
 * KPI cards (PRD FR-6.1, Appendix A).
 *
 * A card carries two readings of its value: the string the model wrote, and the
 * number it meant when it sent one. The number wins — formatted in the
 * deployment's currency with tabular numerals, so a column of cards lines up
 * and a rebrand to euros is a settings change rather than an edit here.
 *
 * **`compact` is what a tile asks for, and it is not merely a smaller version.**
 * A one-row tile frame is 180px, of which the card's header, subtitle and
 * export footer take 116 — leaving a **64px** body. The boxed reading needs
 * ~120px and cuts the number in half, which is the bug this answers; a
 * bordered box inside a bordered card was redundant chrome there anyway.
 *
 * So the compact reading drops the box and moves the delta and the note up
 * beside the label, which leaves the value the full width and makes the strip
 * **two lines — 55px — whatever the tile's width and however many KPIs it
 * holds.** Constant height is the point: it is what makes "height 1 does not
 * clip a KPI" true by construction rather than true for the sizes somebody
 * happened to try.
 *
 * The strip therefore never wraps. Where the KPIs genuinely do not fit across
 * the tile it scrolls sideways, and the label and the note give way before the
 * value does — because a number you have to scroll to is still a number and
 * half a number is not.
 *
 * What this cannot fix from here: the subtitle above it is `flex-wrap`, and
 * below about 250px of tile a second line there takes the body down to 46px.
 * That is the tile's chrome spending the frame, not this renderer, and it
 * costs every artifact type the same way.
 */
export function SummaryCards({ artifact, compact = false }: { artifact: Artifact; compact?: boolean }) {
  const formatter = useFormatter();
  const items = artifact.items ?? [];
  if (!items.length) return null;

  const reading = (item: (typeof items)[number]) =>
    item.value_number !== null && item.value_number !== undefined
      ? formatter.value(item.value_number, item.format as ValueFormat)
      : item.value || "—";

  const spelled = (item: (typeof items)[number]) =>
    item.value_number !== null && item.value_number !== undefined ? undefined : item.value;

  const deltaTone = (direction: string | null | undefined) =>
    cn(
      direction === "up" && "text-success",
      direction === "down" && "text-error",
      !direction && "text-ink-secondary",
    );

  // `safe center` rather than `items-center`: centring inside a scroll container
  // puts the overflow *above* the scroll origin, so a tile whose subtitle has
  // wrapped to three lines would hide the top of every number where no amount of
  // scrolling reaches it. `safe` falls back to flex-start exactly when it would
  // not fit, which is the only case it changes. Tailwind has no utility for it.
  if (compact) {
    return (
      <div className="flex h-full gap-5 overflow-x-auto px-3 py-1 [align-items:safe_center]">
        {items.map((item, index) => (
          <div key={`${item.label}-${index}`} className="flex min-w-44 flex-1 flex-col gap-0.5">
            <span className="flex min-w-0 items-baseline gap-1.5">
              <span className="type-caption text-ink-secondary min-w-0 truncate">{item.label}</span>
              {item.delta ? (
                <span className={cn("type-caption shrink-0 tabular-nums", deltaTone(item.direction))}>
                  {item.delta}
                </span>
              ) : null}
              {item.note ? (
                <span className="type-caption text-ink-disabled min-w-0 truncate">{item.note}</span>
              ) : null}
            </span>
            <span className="type-metric text-ink truncate" title={spelled(item)}>
              {reading(item)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-3 p-3">
      {items.map((item, index) => (
        <div
          key={`${item.label}-${index}`}
          className="border-border rounded-element flex min-w-0 flex-col gap-1 border p-3"
        >
          <span className="type-caption text-ink-secondary truncate">{item.label}</span>
          <span className="type-metric text-ink truncate" title={spelled(item)}>
            {reading(item)}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {item.delta ? (
              <span className={cn("type-support tabular-nums", deltaTone(item.direction))}>
                {item.delta}
              </span>
            ) : null}
            {item.note ? <span className="type-caption text-ink-disabled">{item.note}</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}
